import {
  FC,
  useCallback,
  CSSProperties,
  useEffect,
  useRef,
  ReactNode
} from 'react'
import { composeClasses } from '@/utils/compose-classes'
import { generateError } from './utils'
import { PartialIconSvg, CheckedIconSvg } from './assets'
import { Flex } from '../flex'
import { AsType } from '../as'
import {
  AlignItemsType,
  FlexDirectionType,
  GapType,
  JustifyContentType
} from '@/interfaces/types'

export interface CheckboxProps {
  isChecked?: boolean
  as?: AsType
  items?: AlignItemsType
  justify?: JustifyContentType
  dir?: FlexDirectionType
  gap?: GapType
  onChange: (value: boolean, name?: string) => void
  type: 'checkbox' | 'radio'
  isPartial?: boolean
  isDisabled?: boolean
  name?: string
  inputClassName?: string
  containerClassName?: string
  labelClassName?: string
  inputStyle?: CSSProperties
  id?: string
  label?: ReactNode
}

export const Checkbox: FC<CheckboxProps> = ({
  isChecked,
  onChange,
  type,
  isDisabled = false,
  isPartial,
  inputClassName,
  containerClassName,
  labelClassName,
  name,
  inputStyle,
  id,
  label,
  as,
  items,
  justify,
  dir,
  gap = 'gap-1'
}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const isRadio = Boolean(type === 'radio')

  const checkboxIsChecked = Boolean(isChecked || isPartial)

  const handleOnClick = useCallback(() => {
    onChange(!checkboxIsChecked, name)
  }, [onChange, checkboxIsChecked, name])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = Boolean(isPartial)
    }
  }, [isPartial])

  useEffect(() => {
    generateError({
      isChecked,
      type,
      isPartial
    })
  }, [isChecked, type, isPartial])

  return (
    <Flex
      data-testid="checkbox-container"
      as={as}
      onClick={handleOnClick}
      dir={dir}
      gap={gap}
      items={items}
      justify={justify}
      className={composeClasses(
        containerClassName,
        'relative transition-all duration-300 ease-in-out',
        isDisabled && 'pointer-events-none cursor-default'
      )}
    >
      <input
        id={id}
        name={name}
        ref={inputRef}
        type={type}
        checked={isChecked}
        disabled={isDisabled}
        className={composeClasses(
          inputClassName,
          isRadio ? 'rounded-full' : 'rounded-[4px]',
          Boolean(isRadio && checkboxIsChecked) && 'shadow-radio-inset-white',
          isDisabled && 'border-terciary hover:bg-transparent',
          Boolean(isDisabled && checkboxIsChecked) &&
            '!bg-terciary checked:!bg-terciary',
          checkboxIsChecked
            ? '!bg-primary checked:bg-primary text-white outline outline-transparent outline-2 hover:outline-surface-gray-20'
            : 'hover:bg-surface-gray-20',
          'appearance-none w-6 h-6 border-2 border-primary cursor-pointer text-white bg-white transition-all duration-300 ease-in-out'
        )}
        style={inputStyle}
      />
      {label && (
        <label
          onClick={(event) => event.stopPropagation()}
          data-testid="checkbox-label"
          className={composeClasses(
            labelClassName,
            isDisabled ? 'text-terciary' : 'cursor-pointer'
          )}
          htmlFor={id}
        >
          {label}
        </label>
      )}
      {isPartial && !isChecked && <PartialIconSvg />}
      {isChecked && !isRadio && <CheckedIconSvg />}
    </Flex>
  )
}

export default Checkbox
